import { useCallback } from "react";

import { lightTheme } from "@hl/base-components/lib/theme/light/theme";
import CopyButton from "@hl/shared-features/lib/features/widgets/CopyButton";
import {
  buildContractTokenUrl,
  getBlockExplorerName,
} from "@hl/shared-features/lib/utils/blockExplorer";
import { maskAddress } from "@hl/shared-features/lib/utils/content";
import {
  buildOpenSeaLinkUrl2,
  getNetworkTypeForChainId,
} from "@hl/shared-features/lib/utils/opensea";
import { isAudioUrl } from "@hl/shared-features/lib/utils/urls";
import {
  Box,
  createStyles,
  Title,
  Group,
  Button,
  UnstyledButton,
  Loader,
  Text,
} from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { get } from "lodash";

import { PortfolioItem } from "../../apollo/graphql.generated";
import { ReactComponent as CopyIcon } from "../../assets/icons/copy-white.svg";
import { MINT_BASE_URL } from "../../config";
import { useHideScrollbar } from "../../styles/scrollbar";
import { normalizeNftUrl } from "../../utils/image-utils";

import { usePortfolioItemMetadataQuery } from "./queries/portfolio.graphql.generated";

type Props = {
  item: PortfolioItem;
  portfolio: {
    slug: string;
    artistName?: string | null;
  };
  onClickOutside: () => void;
};

const BUTTON_COLOR = get(lightTheme, ["colors", "primaryButton", "1"]);
const BG_COLOR = "#191919"; // same as theme.colors.black[8] but without opacity

const useStyles = createStyles((theme) => ({
  root: {
    position: "fixed",
    overflowY: "scroll",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: BG_COLOR,
    color: theme.colors.white[9],

    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  content: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    padding: 20,
    margin: "auto",
    [theme.fn.largerThan("sm")]: {
      width: "70%",
    },
  },
  imageBox: {
    textAlign: "center",
    width: "100%",
  },
  image: {
    width: "100%",
    [theme.fn.largerThan("sm")]: {
      width: "auto",
      maxWidth: "70vw",
      maxHeight: "60vh",
    },
  },
  audioImage: {
    [theme.fn.largerThan("sm")]: {
      maxHeight: "calc(60vh - 60px)",
    },
  },
  audioPlayer: {},
  title: {
    fontSize: 24,
    [theme.fn.largerThan("sm")]: {
      fontSize: 35,
    },
  },
  subTitle: {
    fontSize: 18,
    [theme.fn.largerThan("sm")]: {
      fontSize: 24,
    },
  },
}));

const formatStandard = (input: string) =>
  input.replace(/^([A-Z]+)(\d+)$/, "$1-$2");

const formatNetworkType = (network: string) => {
  return network[0] + network.toLowerCase().slice(1);
};

export const ItemDetailsOverlay = ({
  item,
  onClickOutside,
  portfolio,
}: Props) => {
  const { classes, theme, cx } = useStyles();
  useHotkeys([["Escape", onClickOutside]]);
  const stopPropagation = useCallback(
    (e: React.MouseEvent) => e.stopPropagation(),
    []
  );
  useHideScrollbar(BG_COLOR);

  const { data, loading } = usePortfolioItemMetadataQuery({
    variables: {
      slug: portfolio.slug,
      itemId: item.id,
    },
  });
  const metadata = data?.getPortfolioItemMetadata;
  const name = item.name ?? item.collectionName;

  const col = item.collectionInfo;
  const networkType = getNetworkTypeForChainId(col.chainId);
  const isAudio = isAudioUrl(item.videoUrl);
  return (
    <div className={classes.root} onClick={onClickOutside}>
      <div className={classes.content}>
        <Title
          order={2}
          align="center"
          color={"white.9"}
          className={classes.title}
        >
          {name}
        </Title>
        <Group>
          {item.collectionName && name !== item.collectionName && (
            <>
              <Text color={"white.9"} className={classes.subTitle}>
                {item.collectionName}
                {portfolio.artistName && " · "}
              </Text>
            </>
          )}
          {portfolio.artistName && (
            <Text color={"white.9"} className={classes.subTitle}>
              {portfolio.artistName}
            </Text>
          )}
        </Group>

        <Group onClick={stopPropagation} mt={8} align="center">
          {col.tokenId && (
            <>
              Token ID{" "}
              {col.tokenId.length > 5 ? (
                <>
                  {maskAddress(col.tokenId, 2, 2)}
                  <CopyButton copyValue={col.tokenId} Icon={<CopyIcon />} />
                </>
              ) : (
                col.tokenId
              )}
              {" · "}
            </>
          )}
          {formatStandard(col.standard)} {" · "}
          {formatNetworkType(networkType)} {" · "}
          <span>{maskAddress(col.address, 6, 4)}</span>
          <CopyButton copyValue={col.address} Icon={<CopyIcon />} />
        </Group>

        <Box
          className={classes.imageBox}
          mt={24}
          onClick={item.videoUrl ? stopPropagation : undefined}
        >
          {item.videoUrl ? (
            isAudio ? (
              <>
                <img
                  src={normalizeNftUrl(item.imageUrl, { size: 1500 })}
                  className={cx([classes.image, classes.audioImage])}
                />
                <div>
                  <audio
                    src={item.videoUrl}
                    controls
                    autoPlay
                    className={classes.audioPlayer}
                  />
                </div>
              </>
            ) : (
              <video
                className={classes.image}
                autoPlay
                loop
                playsInline
                controls={true}
                controlsList="nodownload"
              >
                <source src={item.videoUrl} />
              </video>
            )
          ) : (
            <img
              src={normalizeNftUrl(item.imageUrl, { size: 1500 })}
              className={classes.image}
            />
          )}
        </Box>
        <Box sx={{ whiteSpace: "pre-wrap" }} mt={24}>
          {item.description}
        </Box>
        <div onClick={stopPropagation}>
          <Group pt={20} position="center">
            {item.collectionId && (
              <Button
                component="a"
                href={MINT_BASE_URL + `/${item.collectionId}`}
                target="_blank"
                sx={{ backgroundColor: BUTTON_COLOR }}
              >
                View mint page
              </Button>
            )}
            {item.openSeaLink && (
              <Button
                component="a"
                href={buildOpenSeaLinkUrl2(
                  col.address,
                  col.chainId,
                  col.tokenId
                )}
                target="_blank"
                sx={{ backgroundColor: BUTTON_COLOR }}
              >
                View on OpenSea
              </Button>
            )}
            {item.button && (
              <Button
                component="a"
                href={item.button.url}
                target="_blank"
                sx={{ backgroundColor: BUTTON_COLOR }}
              >
                {item.button.text}
              </Button>
            )}
          </Group>
          <Group m="lg" position="center">
            {loading && <Loader size="sm" />}
            {metadata?.imageOriginalUrlHttp && (
              <>
                <UnstyledButton
                  component="a"
                  href={metadata.imageOriginalUrlHttp}
                  target="_blank"
                  sx={{ color: theme.colors.white[9] }}
                >
                  View original source
                </UnstyledButton>
                {" · "}
              </>
            )}

            <UnstyledButton
              component="a"
              href={buildContractTokenUrl(networkType, col.address)}
              target="_blank"
              sx={{ color: theme.colors.white[9] }}
            >
              View on {getBlockExplorerName(networkType)}
            </UnstyledButton>
          </Group>
        </div>
      </div>
    </div>
  );
};
